import { createContext, useContext, useState, useMemo, type ReactNode, type FC } from 'react';

import type { GymStructure } from 'app/types/graphql';

interface GymStructureContextType {
  gymStructure: GymStructure | undefined;
  isLoading: boolean;
  setGymStructure: (gymStructure: GymStructure | undefined) => void;
  setLoading: (isLoading: boolean) => void;
}

const GymStructureContext = createContext<GymStructureContextType | undefined>(undefined);

interface GymStructureProviderProps {
  children: ReactNode;
}

export const GymStructureProvider: FC<GymStructureProviderProps> = ({ children }) => {
  const [gymStructure, setGymStructure] = useState<GymStructure | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(true);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({ gymStructure, isLoading, setGymStructure, setLoading }),
    [gymStructure, isLoading],
  );

  return <GymStructureContext.Provider value={contextValue}>{children}</GymStructureContext.Provider>;
};

export const useGymStructure = (): GymStructureContextType => {
  const context = useContext(GymStructureContext);
  if (context === undefined) {
    throw new Error('useGymStructure must be used within a GymStructureProvider');
  }
  return context;
};
